import { Component, input, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { CognitoService } from 'src/app/core/service/cognito.service';
import {
  SidebarToggleObservable,
  CreditCountObservable,
  UserInfoObservable,
} from 'src/app/core/service/observable.service';
import { TablesService } from 'src/app/core/service/table.service';
import { UserInfo } from 'src/app/core/service/user.service';
import { ToastService } from 'src/app/toast/toast.service';
import { LocalStorageService } from 'src/app/core/service/local-storage.service';

@Component({
  selector: 'app-account-menu',
  templateUrl: './account-menu.component.html',
  styleUrl: './account-menu.component.scss',
  standalone: false,
})
export class AccountMenuComponent {
  @Input() toggleSidebar!: () => void;
  isExpanded = input<boolean>();
  @ViewChild('accountDropdown') accountDropdown!: NgbDropdown;
  subscription: Subscription = new Subscription();
  creditCountSubscription: Subscription = new Subscription();
  userInfoSubscription: Subscription = new Subscription();
  sidebarToggleSubscription: Subscription = new Subscription();
  dataset: any;
  activeTab = 'all'; // Default active tab
  hideSideBarChevron: boolean = false;
  _creditCount: number = 0;
  creditCountLevel: 'none' | 'low' | 'medium' | 'high' = 'none';
  _userInfo: UserInfo = {} as UserInfo;
  sidebarToggled: boolean = false;
  maxCredits: number = 1000; 

  get isFreemiumUser(): boolean {
    return this._userInfo.subscriptionTier === 't2'; // t2 is FREEMIUM tier
  }

  get isDemoAccount(): boolean {
    return this._userInfo?.accessLevel === 'Demo';
  }

  get creditCount() {
    return this._creditCount;
  }
  set creditCount(value: number) {
    this._creditCount = value;
    this.creditCountLevel = value > 5 ? 'high' : value > 2 ? 'low' : 'none';
  }

  get userInfo() {
    return this._userInfo;
  }
  set userInfo(value: UserInfo) {
    this._userInfo = value;
  }

  constructor(
    public cognitoService: CognitoService,
    private router: Router,
    private tableService: TablesService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private sidebarToggleObservable: SidebarToggleObservable,
    private creditCountObservable: CreditCountObservable,
    private userInfoObservable: UserInfoObservable,
    private localStorageService: LocalStorageService
  ) {}
  ngOnInit() {
    this.subscription = this.tableService.getData().subscribe((data: any) => {
      this.dataset = data;
    });

    this.subscription = this.tableService
      .getUpdateFromSpreadsheet()
      .subscribe((data: any) => {
        this.dataset = data;
      });

    this.creditCountSubscription = this.creditCountObservable
      .updates()
      .subscribe((data: number) => {
        this.creditCount = data;
        if (this.userInfo) {
          this.userInfo.credits = data;
        }
      });

    this.userInfoSubscription = this.userInfoObservable
      .updates()
      .subscribe((data: UserInfo) => {
        console.log('HeaderComponent:userInfoSubscription', data);
        this.userInfo = data;
      });

    this.sidebarToggleSubscription =
      this.sidebarToggleObservable.toggled$.subscribe((toggled: boolean) => {
        this.sidebarToggled = toggled;
      });

    this.initializeSidebarState();
    this.creditCount = this.creditCountObservable.get();
    this.userInfo = this.userInfoObservable.get();
  }
  logout() {
    this.cognitoService.signOut().then((res: any) => {
      window.sessionStorage.clear();
      window.localStorage.clear();
      window.location.href = '/auth/signIn';
    });
  }
  openGetStarted() {
    window.open('https://www.talonic.com/get-started', '_blank');
  }

  editTableName(table: any) {
    table.tableName = this.dataset.tableName;
    table.editedSheetName = table.tableName;
    table.isSheetNameEdit = true;
    return;
  }

  duplicateTable(table: any) {
    this.tableService
      .duplicateTable(this.dataset.tableId, table)
      .subscribe((res: any) => {
        this.toastService.show('info', 'Table is duplicated successfully.', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
        this.tableService.setUpdateNotification(res);
      });
  }
  deleteId: any;
  confirm(content: any, id: any) {
    this.deleteId = id;
    this.modalService.open(content, { centered: true });
  }

  deleteItem(deleteId: any) {
    this.tableService.deleteTable(this.deleteId).subscribe((res: any) => {
      this.localStorageService.clearInterruptedUploadForTable(this.deleteId);
      // setting blank
      this.dataset.tableName = '';
      this.tableService.setUpdateNotification(this.dataset);
      this.toastService.show('info', 'Table is deleted.', {
        classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
      });
    });
  }

  stopEditing(table: any) {
    table.tableName = table.editedSheetName;
    this.tableService
      .updateTable(table.tableId, { tableName: table.editedSheetName })
      .subscribe((res: any) => {
        table.isSheetNameEdit = false;
        this.toastService.show('info', 'Table Name updated', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
        this.tableService.setUpdateNotification(table);
      });
  }

  setActiveTab(tabName: string) {
    this.activeTab = tabName;
  }

  onAccountMenuClick(event: Event) {
    if (!this.isExpanded()) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleSidebar();
    } else {
      if (this.accountDropdown) {
        if (this.accountDropdown.isOpen()) {
          this.accountDropdown.close();
        } else {
          this.accountDropdown.open();
        }
      }
    }
  }

  toggleSidebarViaObservable() {
    if (this.accountDropdown) {
      this.accountDropdown.close();
    }
    this.sidebarToggleObservable.toggleSidebar();
  }

  getCreditProgress(): number {
    const creditsLeft = this.userInfo?.credits ?? this._creditCount ?? 0;
    const denom =
      (this.userInfo && this.userInfo.initialCredits) ? this.userInfo.initialCredits! : (this.maxCredits || 1000);
    const pct = (creditsLeft / denom) * 100;
    if (Number.isNaN(pct)) {
      return 0;
    }
    return Math.max(0, Math.min(100, pct));
  }

  getUserInitial() {
    return this.userInfo?.firstName?.toUpperCase()[0] || ' '; // <- that's a non-breaking space
  }

  openModal(content: any) {
    this.modalService.open(content, { centered: true });
  }

  initializeSidebarState() {
    const savedExpanded = localStorage.getItem('sidebarExpanded');
    if (savedExpanded !== null) {
      this.sidebarToggled = JSON.parse(savedExpanded);
      console.log(
        '🔍 ACCOUNT-MENU DEBUG - initialized sidebar state from localStorage:',
        this.sidebarToggled
      );
    } else {
      // Default to true (expanded) if no saved state
      this.sidebarToggled = true;
      console.log(
        '🔍 ACCOUNT-MENU DEBUG - initialized sidebar state to default (true)'
      );
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.creditCountSubscription.unsubscribe();
    this.userInfoSubscription.unsubscribe();
    this.sidebarToggleSubscription.unsubscribe();
  }
}
